/**
 * The offline write queue engine.
 *
 * Two entry points:
 *   - runOrQueue(kind, payload): attempt the write now if online; on a
 *     network failure (or if offline) persist it to the queue instead. Used
 *     by mutations so the happy path stays synchronous-feeling.
 *   - flush(): replay everything pending, in FIFO order, when back online.
 *
 * It exposes a tiny observable so the React layer can render a pending/offline
 * indicator (subscribe/getSnapshot) and react to successful syncs
 * (subscribeSynced) to invalidate the right queries.
 */
import { deleteItem, getAllItems, putItem } from "./db";
import { executors, isRetryableError } from "./executors";
import type { OpKind, QueueItem, QueueSnapshot } from "./types";

const MAX_ATTEMPTS = 10;
// Photos get a bigger budget: they're order-independent, idempotent, and
// "weak-but-online" venues (captive portals, concrete basements) can burn
// through 10 attempts in minutes while the signal is genuinely coming back.
const PHOTO_MAX_ATTEMPTS = 30;

// ---- observable snapshot (pending/failed/flushing) -------------------------
type Listener = () => void;
const listeners = new Set<Listener>();
const syncedListeners = new Set<(item: QueueItem) => void>();

let snapshot: QueueSnapshot = { pending: 0, failed: 0, flushing: false };

function setSnapshot(next: Partial<QueueSnapshot>) {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => l());
}

export function subscribeQueue(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function getQueueSnapshot(): QueueSnapshot {
  return snapshot;
}

/** Fires after an item is successfully replayed from the queue (not the
 *  immediate-online path) so the UI can invalidate the matching queries. */
export function subscribeSynced(cb: (item: QueueItem) => void): () => void {
  syncedListeners.add(cb);
  return () => syncedListeners.delete(cb);
}

async function refreshCounts(): Promise<void> {
  const items = await getAllItems();
  let pending = 0;
  let failed = 0;
  for (const i of items) {
    if (i.failed) failed++;
    else pending++;
  }
  setSnapshot({ pending, failed });
}

/** Recompute counts from IndexedDB on startup (survives reloads). */
export async function initQueue(): Promise<void> {
  await refreshCounts();
}

export async function enqueue(
  kind: OpKind,
  payload: Record<string, unknown>,
): Promise<void> {
  const item: QueueItem = {
    id: crypto.randomUUID(),
    kind,
    payload,
    createdAt: new Date().toISOString(),
    attempts: 0,
  };
  await putItem(item);
  await refreshCounts();
  // If we're online (e.g. a dependent op queued because its check-in hadn't
  // synced yet), kick a flush so it drains promptly rather than waiting for
  // the retry interval.
  if (navigator.onLine) void flush();
}

/**
 * Try the write immediately when online; fall back to the durable queue on a
 * network failure or when offline. Throws only on a PERMANENT error (e.g. a
 * constraint/RLS rejection) so the caller's onError can surface it; a queued
 * write resolves with { queued: true }.
 */
export async function runOrQueue(
  kind: OpKind,
  payload: Record<string, unknown>,
): Promise<{ queued: boolean }> {
  const exec = executors[kind];
  if (navigator.onLine && exec) {
    try {
      await exec(payload, {
        id: "inline",
        kind,
        payload,
        createdAt: new Date().toISOString(),
        attempts: 0,
      });
      return { queued: false };
    } catch (err) {
      if (!isRetryableError(err)) throw err; // permanent → surface to caller
      // transient/offline → fall through and persist
    }
  }
  await enqueue(kind, payload);
  return { queued: true };
}

let flushing = false;

/** Replay all pending items. Safe to call repeatedly / concurrently. */
export async function flush(): Promise<void> {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  setSnapshot({ flushing: true });
  try {
    const all = (await getAllItems()).filter((i) => !i.failed);
    // Payroll chain first, photos LAST: a multi-MB upload stalling on weak
    // signal must never sit in front of a queued checkout/hours submission.
    // (Photos are order-independent by design; everything else keeps FIFO.)
    const items = [
      ...all.filter((i) => i.kind !== "checkin_photo"),
      ...all.filter((i) => i.kind === "checkin_photo"),
    ];
    for (const item of items) {
      if (!navigator.onLine) break; // went offline mid-flush
      const exec = executors[item.kind];
      if (!exec) continue; // kind not wired yet — leave it for a later build
      try {
        await exec(item.payload, item);
        await deleteItem(item.id);
        await refreshCounts();
        syncedListeners.forEach((l) => l(item));
      } catch (err) {
        const retryable = isRetryableError(err);
        item.attempts += 1;
        item.lastError = err instanceof Error ? err.message : String(err);
        const cap = item.kind === "checkin_photo" ? PHOTO_MAX_ATTEMPTS : MAX_ATTEMPTS;
        if (!retryable || item.attempts >= cap) {
          item.failed = true;
        }
        await putItem(item);
        await refreshCounts();
        // A network blip will hit every remaining item — stop this pass and
        // let the next online/tick retry, preserving order. EXCEPT photos:
        // nothing depends on them, and a big upload timing out on weak signal
        // must never starve the checkout/submit ops queued behind it — skip
        // past and keep draining the payroll chain.
        if (retryable && item.kind !== "checkin_photo") break;
      }
    }
  } finally {
    flushing = false;
    setSnapshot({ flushing: false });
  }
}

// ---- failed-item recovery (backs the "Sync problems" sheet) ----------------

/** The permanently-failed items, so the UI can list them with a reason. */
export async function listFailed(): Promise<QueueItem[]> {
  return (await getAllItems()).filter((i) => i.failed);
}

/** Un-fail a single item and re-flush so it gets another chance. */
export async function retryItem(id: string): Promise<void> {
  const item = (await getAllItems()).find((i) => i.id === id);
  if (!item) return;
  delete item.failed;
  item.attempts = 0;
  delete item.lastError;
  await putItem(item);
  await refreshCounts();
  void flush();
}

/** Un-fail every failed item and re-flush. */
export async function retryAllFailed(): Promise<void> {
  const failedItems = (await getAllItems()).filter((i) => i.failed);
  for (const item of failedItems) {
    delete item.failed;
    item.attempts = 0;
    delete item.lastError;
    await putItem(item);
  }
  await refreshCounts();
  void flush();
}

/** Permanently discard a failed item the staffer chooses not to retry. */
export async function dismissItem(id: string): Promise<void> {
  await deleteItem(id);
  await refreshCounts();
}

/**
 * Auto-recovery for photos on reconnect: un-fail any failed checkin_photo
 * items and re-flush. Photos are order-independent and idempotent, and their
 * "failure" is very often a weak-but-online venue burning the attempt budget —
 * a genuine connectivity change deserves a fresh start without requiring the
 * staffer to find the Sync problems sheet.
 */
export async function retryFailedPhotos(): Promise<void> {
  const failedPhotos = (await getAllItems()).filter(
    (i) => i.failed && i.kind === "checkin_photo",
  );
  if (failedPhotos.length === 0) return;
  for (const item of failedPhotos) {
    delete item.failed;
    item.attempts = 0;
    delete item.lastError;
    await putItem(item);
  }
  await refreshCounts();
  void flush();
}
